import { makeAutoObservable, runInAction } from "mobx";
import bots from "./bots";
import auth from "./auth";
import {
  addMbaAllowlist, addMbaWebsite, createMbaSkill, deleteMbaSkill, getMbaAllowlist, getMbaBusinessInfo, getMbaFiles,
  getMbaKnowledge, getMbaManagementAccess, getMbaReadiness, getMbaSettings, getMbaSkills, getMbaWebsites,
  MbaAgentTestResult, MbaAllowlistEntry, MbaBotCredentials, MbaBusinessInfo, MbaFile, MbaKnowledge, MbaKnowledgePreview, MbaManagementAccess,
  MbaReadiness, MbaSettings, MbaSkill, MbaSkillInput, MbaWebsite,
  onboardMba, previewMbaKnowledge, publishMbaKnowledge, removeMbaAllowlist, setMbaRollout,
  removeMbaWebsite, resetMbaBusinessInfo, testMbaAgent, updateMbaBusinessInfo, updateMbaSettings, updateMbaSkill,
  getMbaFaqSync, setMbaFaqSync, retryMbaFaqSync, getMbaOwnership, recoverMbaOwnership, releaseMbaThread, MbaFaqSync, MbaOwnership,
} from "../services/metaBusinessAgent";

class MetaBusinessAgentStore {
  readiness?: MbaReadiness;
  knowledge?: MbaKnowledge;
  settings?: MbaSettings;
  allowlist: MbaAllowlistEntry[] = [];
  preview?: MbaKnowledgePreview;
  testResult?: MbaAgentTestResult;
  managementAccess?: MbaManagementAccess;
  businessInfo: MbaBusinessInfo = {};
  websites: MbaWebsite[] = [];
  files: MbaFile[] = [];
  skills: MbaSkill[] = [];
  managementAccessChecked = false;
  loading = false;
  action?: string;
  error?: string;
  resourceErrors: Record<string, string> = {};
  publishResult?: Record<string, number>;
  faqSync?: MbaFaqSync;
  ownership?: MbaOwnership;
  operationsLoading = false;
  private generation = 0;
  private actionGeneration = 0;
  private requestController?: AbortController;

  constructor() { makeAutoObservable(this); }
  get canManage() { return Boolean(this.managementAccess?.allowed); }
  private get bot() {
    const bot = bots.selectedBot;
    if (!bot) throw new Error("Select a bot first");
    return { id: bot.id, key: bot.key, password: bot.password };
  }

  load = async (includeResources = true) => {
    const selected = bots.selectedBot;
    if (!selected) return;
    const generation = ++this.generation;
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    const current = () => generation === this.generation && bots.selectedBot?.id === selected.id;
    this.loading = true;
    this.error = undefined;
    try {
      const credentials = this.bot;
      const readiness = await getMbaReadiness(credentials, controller.signal);
      if (!current()) return;
      this.readiness = readiness;
      if (readiness.connected) {
        const [knowledge, access] = await Promise.allSettled([
          getMbaKnowledge(credentials, controller.signal),
          getMbaManagementAccess(credentials, controller.signal),
        ]);
        if (!current()) return;
        this.managementAccessChecked = true;
        if (knowledge.status === "fulfilled") {
          this.knowledge = knowledge.value;
          delete this.resourceErrors.knowledge;
        } else {
          this.resourceErrors.knowledge = "Could not load Meta FAQs. Refresh to try again.";
        }
        if (access.status === "fulfilled") {
          this.managementAccess = access.value;
          console.info("[MBA] Backend management access granted", {
            userId: access.value.user_id,
            backendUserType: access.value.user_type,
            frontendUserType: auth.user?.type,
          });
        } else {
          this.managementAccess = undefined;
          console.warn("[MBA] Backend management access denied", {
            frontendUserId: auth.user?.id,
            frontendUserType: auth.user?.type,
            error: access.reason instanceof Error ? access.reason.message : String(access.reason),
          });
        }
        if (this.canManage && readiness.local.configured) {
          const requests = [getMbaSettings(credentials, controller.signal) as Promise<unknown>];
          if (includeResources) requests.push(
            getMbaAllowlist(credentials, controller.signal),
            getMbaBusinessInfo(credentials, controller.signal),
            getMbaWebsites(credentials, controller.signal),
            getMbaFiles(credentials, controller.signal),
            getMbaSkills(credentials, controller.signal),
            getMbaFaqSync(credentials, controller.signal),
            getMbaOwnership(credentials, controller.signal),
          );
          const results = await Promise.allSettled(requests);
          if (!current()) return;
          const resources = ["settings", "allowlist", "business", "websites", "files", "skills", "faqSync", "ownership"];
          results.forEach((result, index) => {
            if (result.status === "rejected") this.resourceErrors[resources[index]] = `Could not load ${resources[index]}. Refresh before editing.`;
            else delete this.resourceErrors[resources[index]];
          });
          if (results[0].status === "fulfilled") this.settings = results[0].value as MbaSettings;
          if (includeResources) {
            if (results[1].status === "fulfilled") this.allowlist = results[1].value as MbaAllowlistEntry[];
            if (results[2].status === "fulfilled") this.businessInfo = results[2].value as MbaBusinessInfo;
            if (results[3].status === "fulfilled") this.websites = results[3].value as MbaWebsite[];
            if (results[4].status === "fulfilled") this.files = results[4].value as MbaFile[];
            if (results[5].status === "fulfilled") this.skills = results[5].value as MbaSkill[];
            if (results[6].status === "fulfilled") this.faqSync = results[6].value as MbaFaqSync;
            if (results[7].status === "fulfilled") this.ownership = results[7].value as MbaOwnership;
          }
        }
      }
    } catch (error) {
      if (!current() || (error instanceof Error && error.name === "AbortError")) return;
      this.error = error instanceof Error ? error.message : "MBA status unavailable";
    } finally { if (current()) this.loading = false; }
  };

  private run = async <T>(name: string, operation: (bot: MbaBotCredentials) => Promise<T>, apply?: (result: T) => void) => {
    if (this.action) throw new Error("Wait for the current action to finish.");
    if (!this.canManage) throw new Error("Administrator access is required.");
    const credentials = this.bot;
    const generation = ++this.actionGeneration;
    const current = () => generation === this.actionGeneration
      && bots.selectedBot?.id === credentials.id
      && bots.selectedBot?.key === credentials.key
      && bots.selectedBot?.password === credentials.password;
    this.action = name;
    this.error = undefined;
    try {
      const result = await operation(credentials);
      if (!current()) throw new DOMException("The selected bot changed.", "AbortError");
      runInAction(() => apply?.(result));
      return result;
    } catch (error) {
      if (current()) runInAction(() => { this.error = error instanceof Error ? error.message : "MBA action failed"; });
      throw error;
    } finally {
      if (current()) runInAction(() => { this.action = undefined; });
    }
  };
  onboard = async () => { await this.run("onboard", onboardMba); await this.load(); };
  saveSettings = (settings: Partial<MbaSettings>) => this.run("settings", bot => updateMbaSettings(bot, settings), result => {
    this.settings = Array.isArray(result) ? result[0] : result;
  });
  testAgent = (message: string) => this.run("test", bot => testMbaAgent(bot, message, this.testResult?.conversation_id), result => { this.testResult = result; });
  previewKnowledge = () => this.run("preview", previewMbaKnowledge, result => {
    this.preview = result;
    this.publishResult = undefined;
  });
  publishKnowledge = (sourceIds: string[]) => this.run("publish", async bot => {
    if (!this.preview) throw new Error("Preview knowledge first");
    const publishableIds = this.preview.items.filter(i => ["create", "update"].includes(i.status)).map(i => i.id);
    const ids = [...new Set(sourceIds.filter(id => publishableIds.includes(id)))];
    if (!ids.length) throw new Error("No publishable FAQ changes");
    const result = await publishMbaKnowledge(bot, ids);
    const sync = await getMbaFaqSync(bot);
    return { result, sync };
  }, ({ result, sync }) => {
    this.publishResult = result;
    this.faqSync = sync;
    this.preview = undefined;
  });
  setFaqSync = (enabled: boolean) => this.run("faq-sync", async bot => {
    await setMbaFaqSync(bot, enabled);
    return getMbaFaqSync(bot);
  }, result => { this.faqSync = result; });
  retryFaqSync = (sourceId: string) => this.run("faq-sync", async bot => {
    await retryMbaFaqSync(bot, [sourceId]);
    return getMbaFaqSync(bot);
  }, result => { this.faqSync = result; });
  recoverOwnership = (ids: number[]) => this.run("ownership", async bot => {
    await recoverMbaOwnership(bot, ids);
    return getMbaOwnership(bot);
  }, result => { this.ownership = result; });
  releaseThread = (id: number) => this.run("ownership", async bot => {
    await releaseMbaThread(bot, id);
    return getMbaOwnership(bot);
  }, result => { this.ownership = result; });
  refreshOperations = async () => {
    if (this.loading || this.action || this.operationsLoading || !this.canManage || !this.readiness?.local.configured) return;
    const bot = this.bot;
    const generation = this.generation;
    const actionGeneration = this.actionGeneration;
    const current = () => generation === this.generation && bot.id === bots.selectedBot?.id;
    this.operationsLoading = true;
    try {
      const results = await Promise.allSettled([getMbaFaqSync(bot, this.requestController?.signal), getMbaOwnership(bot, this.requestController?.signal)]);
      if (!current() || actionGeneration !== this.actionGeneration) return;
      let knowledge: MbaKnowledge | undefined;
      let knowledgeFailed = false;
      if (results[0].status === "fulfilled" && results[0].value.last_sync_at && results[0].value.last_sync_at !== this.faqSync?.last_sync_at) {
        try { knowledge = await getMbaKnowledge(bot, this.requestController?.signal); }
        catch { knowledgeFailed = true; }
      }
      if (!current() || actionGeneration !== this.actionGeneration) return;
      runInAction(() => {
        if (knowledge) { this.knowledge = knowledge; delete this.resourceErrors.knowledge; }
        if (knowledgeFailed) this.resourceErrors.knowledge = "Sync completed, but the Meta FAQ count could not refresh. Use Refresh to try again.";
        if (results[0].status === "fulfilled") { this.faqSync = results[0].value; delete this.resourceErrors.faqSync; }
        else this.resourceErrors.faqSync = "Sync status could not be refreshed. It may be out of date.";
        if (results[1].status === "fulfilled") { this.ownership = results[1].value; delete this.resourceErrors.ownership; }
        else this.resourceErrors.ownership = "Ownership status could not be refreshed. Refresh before recovering.";
      });
    } finally { if (current()) runInAction(() => { this.operationsLoading = false; }); }
  };
  resetTestSession = () => { this.testResult = undefined; this.error = undefined; };
  clearError = () => { this.error = undefined; };
  saveBusinessInfo = (data: MbaBusinessInfo) => this.run("business-info", bot => updateMbaBusinessInfo(bot, data), result => { this.businessInfo = result; });
  resetBusinessInfo = () => this.run("business-info", async bot => {
    await resetMbaBusinessInfo(bot);
    return getMbaBusinessInfo(bot);
  }, result => { this.businessInfo = result; });
  addWebsite = (websiteUrl: string) => this.run("websites", async bot => {
    await addMbaWebsite(bot, websiteUrl);
    return getMbaWebsites(bot);
  }, result => { this.websites = result; });
  removeWebsite = (id: string) => this.run("websites", async bot => {
    await removeMbaWebsite(bot, id);
    return getMbaWebsites(bot);
  }, result => { this.websites = result; });
  createSkill = (skill: MbaSkillInput) => this.run("skills", async bot => {
    await createMbaSkill(bot, skill);
    return getMbaSkills(bot);
  }, result => { this.skills = result; });
  updateSkill = (id: string, changes: Pick<MbaSkill, "title" | "status" | "instruction">) => this.run("skills", async bot => {
    await updateMbaSkill(bot, id, changes);
    return getMbaSkills(bot);
  }, result => { this.skills = result; });
  deleteSkill = (id: string) => this.run("skills", async bot => {
    await deleteMbaSkill(bot, id);
    return getMbaSkills(bot);
  }, result => { this.skills = result; });
  addAllowlist = (phone: string) => this.run("allowlist", async bot => {
    await addMbaAllowlist(bot, phone);
    return getMbaAllowlist(bot);
  }, result => { this.allowlist = result; });
  removeAllowlist = (id: string) => this.run("allowlist", async bot => {
    await removeMbaAllowlist(bot, id);
    return getMbaAllowlist(bot);
  }, result => { this.allowlist = result; });
  rollout = async (audience: "ALLOWLISTED_ONLY" | "EVERYONE" | "DISABLED") => {
    await this.run("rollout", bot => setMbaRollout(bot, audience), result => {
      this.settings = Array.isArray(result) ? result[0] : result;
    });
    await this.load();
  };
  clear = () => {
    this.generation += 1;
    this.actionGeneration += 1;
    this.requestController?.abort();
    this.requestController = undefined;
    this.readiness = this.knowledge = this.settings = this.preview = this.testResult = undefined;
    this.managementAccess = undefined;
    this.managementAccessChecked = false;
    this.allowlist = [];
    this.businessInfo = {};
    this.websites = [];
    this.files = [];
    this.skills = [];
    this.resourceErrors = {};
    this.publishResult = undefined;
    this.faqSync = undefined;
    this.ownership = undefined;
    this.operationsLoading = false;
    this.error = this.action = undefined;
    this.loading = false;
  };
}

export default new MetaBusinessAgentStore();
